import React, { useState, useEffect } from 'react';
import { makeCSS } from '../../utils/makeCSS';
import { SizeVariants } from '../../theme/themes';
import { cx } from '@emotion/css';

/**
 * A user interface element used for selecting a single option from a set of mutually exclusive choices.
 * It presents a list of options, the user can choose one option at a time by clicking on the radio associated with that choice.
 */

export interface RadioProps {
  /**Size of the radio */
  size?: SizeVariants.sm | SizeVariants.md | SizeVariants.lg;
  /**Indicates whether the checkBox is checked or not*/
  value?: boolean;
  /**Indicates whether it has been a change at the component*/
  onChange?(newState: boolean): void;
  /**Active or disabled radio */
  disabled?: boolean;
}
const useCSS = makeCSS(({ theme }) => {
  return {
    root: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxSizing: 'border-box',
      borderRadius: theme.borderRadius.full,
      border: `1px solid ${theme.color.line['02']}`,
      background: theme.color.underlay['01'],
      cursor: 'pointer',
      '&.disabled': {
        opacity: 0.4,
        cursor: 'default',
        border: `1px solid ${theme.color.line['03']}`,
        '&:hover': {
          background: theme.color.underlay['01']
        },
        '&:focus': {
          border: `1px solid ${theme.color.line['03']}`,
          boxShadow: 'none'
        },
        '&:active': {
          background: theme.color.underlay['01']
        }
      },
      '&:hover': {
        background: `linear-gradient(0deg, ${theme.color.overlay.hovered} 0%, ${theme.color.overlay.hovered} 100%), ${theme.color.underlay['01']}`
      },
      '&:focus': {
        border: `1px solid ${theme.color.foreground.primary}`,
        boxShadow: ' 0px 0px 0px 2px rgba(255, 212, 35, 0.12)',
        outline: 'none'
      },
      '&:active': {
        background: `linear-gradient(0deg, ${theme.color.overlay.pressed} 0%, ${theme.color.overlay.pressed} 100%), ${theme.color.underlay['01']}`
      }
    },

    'size-sm': {
      width: theme.spacing[5],
      height: theme.spacing[5]
    },
    'size-md': {
      width: theme.spacing[6],
      height: theme.spacing[6]
    },
    'size-lg': {
      width: theme.spacing[8],
      height: theme.spacing[8]
    },
    active: {
      background: theme.color.overlay.primary,
      '&:hover': {
        background: `linear-gradient(0deg, ${theme.color.overlay.hovered} 0%, ${theme.color.overlay.hovered} 100%), ${theme.color.overlay.primary}`
      },
      '&.disabled': {
        background: theme.color.background.secondary,
        '&:hover': {
          background: theme.color.background.secondary
        }
      }
    },

    //radio style
    radio: {
      position: 'absolute',
      zIndex: 10,
      top: 0,
      bottom: 0,
      margin: 'auto',
      borderRadius: theme.borderRadius.full,
      background: theme.color.background.primary,
      '&.disabled': {
        background: theme.color.white[100]
      }
    },
    'radioSize-sm': {
      width: theme.spacing[2],
      height: theme.spacing[2]
    },
    'radioSize-md': {
      width: 10,
      height: 10
    },
    'radioSize-lg': {
      width: theme.spacing[3],
      height: theme.spacing[3]
    }
  };
});

export const Radio = (props: RadioProps) => {
  const { size = SizeVariants.md, value, onChange, disabled = false } = props;
  const classes = useCSS(props);

  const [checked, setChecked] = useState(value || false);

  useEffect(() => {
    setChecked(value || false);
  }, [value]);

  const handleClick = () => {
    if (disabled) {
      console.log('The radio is disabled');
    } else {
      setChecked(!checked);
      if (onChange) {
        onChange(!checked);
      }
    }
  };

  return (
    <div
      tabIndex={0}
      onClick={handleClick}
      className={cx(
        classes.root,
        classes[`size-${size}`],
        checked ? classes.active : '',
        { disabled: disabled }
      )}
    >
      {checked && (
        <div
          className={cx(classes.radio, classes[`radioSize-${size}`], {
            disabled: disabled
          })}
        ></div>
      )}
    </div>
  );
};
